<script lang="ts">
import { defineComponent as DO_defineComponent } from 'vue';
export default /*#__PURE__*/ DO_defineComponent({
	name: 'ToRange'
});
</script>
<template>
	<span class="to-range" :flex="flex ? flex : null" :class="setClass" :style="setStyle" @touchmove="moveHandle($event)" @touchend="endHandle($event)">
		<span ref="barRef" class="to-range-bar">
			<span ref="progressRef" class="to-range-progress" :style="{ width: endProgress - startProgress + '%', left: startProgress + '%' }">
				<span v-if="start" ref="startRef" class="to-range-handle is-start" @mousedown="onStartButtonDown"></span>
				<span ref="endRef" class="to-range-handle" @mousedown="onButtonDown" @touchstart="endStartHandle($event)"></span>
			</span>
		</span>
		<to-input v-if="inputable" v-model="endProgress" class="to-range-input" :width="inputWidth" :dot="fixed"></to-input>
	</span>
</template>
<script lang="ts" setup>
import { CSSProperties, computed, nextTick, onMounted, ref, watch } from 'vue'
import { ToInput } from '@touchvue/components'


export interface RangeProps {
	flex?: boolean
	mode?: string
	modelValue?: string | number
	disabled?: boolean
	inputable?: boolean
	max?: string | number
	min?: string | number
	ruler?: boolean
	start?: boolean
	step?: string | number
	width?: string | number
	inputWidth?: string | number
	toFixed?: string | number
}
const props = withDefaults(defineProps<RangeProps>(), {
	mode: 'default',
	modelValue: 0,
	disabled: false,
	inputable: false,
	max: 100,
	min: 0,
	flex: false,
	ruler: false,
	start: false,
	step: 1,
	width: '',
	inputWidth: 6,
	toFixed: 0
})

const endProgress = ref(0)
const startProgress = ref(0)
const progressRef = ref(null)
const endRef = ref(null)
const startRef = ref(null)
const barRef = ref(null)
const endDragX = ref(0)
const startDragX = ref(0)
const endProgressWidth = ref(0)
const startLeft = ref(0)
const fixed = ref(0)
const valueWidth = ref(0)
const stepProgress = ref(0)

const emit = defineEmits(['update:modelValue', 'change'])

onMounted(() => {
	valueWidth.value = Number(props.max) - Number(props.min)
	if (props.step) {
		stepProgress.value = Number(((1 / (valueWidth.value / Number(props.step))) * 100).toFixed(2))
	}
	fixed.value = Number(props.toFixed) ? Number(props.toFixed) : getDecimalPlaces(props.min)
})
watch(
	() => endProgress.value,
	val => {
		nextTick(() => {
			let value: string | number = 0
			let valueWidth = Number(props.max) - Number(props.min)
			let startValue = (Number(props.min) + valueWidth * (startProgress.value / 100)).toFixed(fixed.value)
			let endValue = (Number(props.min) + valueWidth * (val / 100)).toFixed(fixed.value)
			if (props.start) {
				value = `${startValue},${Math.max(Number(startValue), Number(endValue))}`
			} else {
				value = endValue
			}
			emit('update:modelValue', value)
		})
	}
)

watch(
	() => startProgress.value,
	val => {
		nextTick(() => {
			let value: string | number = 0
			let valueWidth = Number(props.max) - Number(props.min)
			let startValue = (Number(props.min) + valueWidth * (val / 100)).toFixed(fixed.value)
			let endValue = (Number(props.min) + valueWidth * (endProgress.value / 100)).toFixed(fixed.value)
			if (props.start) {
				value = `${startValue},${Math.max(Number(startValue), Number(endValue))}`
			} else {
				value = endValue
			}
			emit('update:modelValue', value)
		})
	}
)

watch(
	() => props.modelValue,
	val => {
		let valueWidth = Number(props.max) - Number(props.min)
		if (props.start && typeof val === 'string') {
			let arr = val.split(',')
			endProgress.value = ((Number(arr[1]) - Number(props.min)) / valueWidth) * 100
			startProgress.value = ((Number(arr[0]) - Number(props.min)) / valueWidth) * 100
		} else {
			let width = ((Number(val) - Number(props.min)) / valueWidth) * 100
			endProgress.value = width
		}
	},
	{ immediate: true }
)

function getDecimalPlaces(num) {
	// 处理整数（无小数点）的情况
	if (Number.isInteger(num)) return 0

	// 将数字转为字符串并按小数点分割
	const parts = num.toString().split('.')

	// 小数点后部分的长度即为小数位数（若没有小数点，返回0）
	return parts.length > 1 ? parts[1].length : 0
}

const setClass = computed(() => {
	let arr: string[] = []
	if (props.inputable) {
		arr.push(`is-inputable`)
	}
	if (props.disabled) {
		arr.push(`is-disabled`)
	}
	if (props.mode) {
		arr.push(`mode-${props.mode}`)
	}
	return arr
})
const setStyle = computed(() => {
	let obj: CSSProperties = {}
	if (props.width) {
		obj.width = isNaN(Number(props.width)) ? props.width : `${props.width}em`
	}
	return obj
})

function onButtonDown(event: MouseEvent) {
	if (props.disabled) return false
	event.preventDefault()
	onEndDragStart(event)
	window.addEventListener('mousemove', onDragging)
	window.addEventListener('touchmove', onDragging)
	window.addEventListener('mouseup', onDragEnd)
	window.addEventListener('touchend', onDragEnd)
	window.addEventListener('contextmenu', onDragEnd)
}
function onStartButtonDown(event: MouseEvent) {
	if (props.disabled) return false
	event.preventDefault()
	onStartDragStart(event)
	window.addEventListener('mousemove', onStartDragging)
	window.addEventListener('touchmove', onStartDragging)
	window.addEventListener('mouseup', onStartDragEnd)
	window.addEventListener('touchend', onStartDragEnd)
	window.addEventListener('contextmenu', onStartDragEnd)
}
function onStartDragStart(event: MouseEvent) {
	startDragX.value = event.clientX
	startLeft.value = Number(progressRef.value.style.left.split('%')[0])
}
function onEndDragStart(event: MouseEvent) {
	endDragX.value = event.clientX
	endProgressWidth.value = progressRef.value.offsetWidth
}
function onStartDragging(e: MouseEvent) {
	let width = e.clientX - startDragX.value
	let barWidth = barRef.value.offsetWidth
	if (width > 0) {
		let per = startLeft.value + (width / barWidth) * 100
		if (per < endProgress.value) {
			startProgress.value = stepProgress.value * Math.round(per / stepProgress.value)
		}
	} else if (width <= 0) {
		let per = startLeft.value + (width / barWidth) * 100
		if (per <= 0) {
			startProgress.value = 0
		} else {
			startProgress.value = stepProgress.value * Math.round(per / stepProgress.value)
		}
	}
}
function onDragging(e: MouseEvent) {
	let width = e.clientX - endDragX.value //变动值
	let barWidth = barRef.value.offsetWidth //滚动区域总宽度
	let startWidth = (startProgress.value / 100) * barWidth //开始按钮的位置
	if (props.start) {
		let per = (width / barWidth) * 100 + ((endProgressWidth.value + startWidth) * 100) / barWidth
		if (width > 0) {
			if (width > barWidth - startWidth) {
				endProgress.value = 100
			} else {
				if (per <= 100) {
					endProgress.value = stepProgress.value * Math.round(per / stepProgress.value)
				} else {
					endProgress.value = 100
				}
			}
		} else if (width <= 0) {
			if (Math.abs(width) > barWidth - startWidth) {
				endProgress.value = startProgress.value
			} else {
				endProgress.value = stepProgress.value * Math.round(per / stepProgress.value)
			}
		}
	} else {
		if (width > 0) {
			if (width > barWidth) {
				endProgress.value = 100
			} else {
				let per = (endProgressWidth.value / barWidth + width / barWidth) * 100
				if (per <= 100) {
					endProgress.value = stepProgress.value * Math.round(per / stepProgress.value)
				} else {
					endProgress.value = 100
				}
			}
		} else if (width <= 0) {
			let per = (width / barWidth) * 100 + (endProgressWidth.value / barWidth) * 100
			if (per >= 0) {
				endProgress.value = stepProgress.value * Math.round(per / stepProgress.value)
			} else if (per < 0) {
				endProgress.value = 0
			}
		}
	}
}
function onDragEnd() {
	window.removeEventListener('mousemove', onDragging)
	window.removeEventListener('touchmove', onDragging)
	window.removeEventListener('mouseup', onDragEnd)
	window.removeEventListener('touchend', onDragEnd)
	window.removeEventListener('contextmenu', onDragEnd)
}
function onStartDragEnd() {
	window.removeEventListener('mousemove', onStartDragging)
	window.removeEventListener('touchmove', onStartDragging)
	window.removeEventListener('mouseup', onStartDragEnd)
	window.removeEventListener('touchend', onStartDragEnd)
	window.removeEventListener('contextmenu', onStartDragEnd)
}
function endStartHandle(event) {
	console.log('endStartHandle', event)
}
function moveHandle(event) {
	console.log('moveHandle', event)
}
function endHandle(event) {
	console.log('moveHandle', event)
}
</script>
